/* eslint-disable @typescript-eslint/no-non-null-assertion */
import './index.css';

import { DEFAULT_THEME, getRemFromPx } from '@4giving/components';
import { MuiThemeProvider } from '@material-ui/core';
import { StylesProvider, createGenerateClassName } from '@material-ui/core/styles';
import * as Sentry from '@sentry/react';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import React from 'react';
import ReactDOM from 'react-dom';

import { Auth0Provider } from './_shared/utility/auth0/react-auth0-spa';
import history from './core/history';
import Router from './core/router';

// Create a class name generator with a custom seed to avoid collisions with component library
const generateClassName = createGenerateClassName({
	productionPrefix: 'donation-',
	seed: 'donation'
});

const traceRate = (): number => {
	if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
		return 0;
	} else if (import.meta.env.VITE_ENVIRONMENT === 'staging') {
		return 1;
	} else if (import.meta.env.VITE_ENVIRONMENT === 'prod') {
		return 1;
	}
	return 0;
};


if (import.meta.env.VITE_ENVIRONMENT !== 'dev' && import.meta.env.VITE_SENTRY_RELEASE) {
	Sentry.init({
		dsn: 'https://44d61392adc5473abc9c60120d34c0bd@o271157.ingest.sentry.io/5265150',
		release: import.meta.env.VITE_SENTRY_RELEASE as string,
		environment: import.meta.env.VITE_ENVIRONMENT as string,

		// This enables automatic instrumentation (highly recommended), but is not
		// necessary for purely manual usage
		integrations: [
			new TracingIntegrations.BrowserTracing({
				routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
			})
		],

		// To set a uniform sample rate
		tracesSampleRate: traceRate(),

		// Filter out known third-party library errors that don't affect user experience
		ignoreErrors: [
			// Mapbox GL JS throws this on mobile Safari when mouseout events have invalid coordinates
			// This is a known issue in the library and doesn't affect functionality
			/Invalid LngLat object/,
			// Facebook/Instagram in-app browser on iOS injects WebKit bridge code that
			// fails when expected native message handlers aren't registered
			/webkit\.messageHandlers/,
			/SCDynimacBridge/,
			/SCDynamicBridge/
		]
	});
}

const rootEl = document.getElementById('root');

DEFAULT_THEME.typography.h1 = {
	...DEFAULT_THEME.typography.h1,
	[DEFAULT_THEME.breakpoints.down('md')]: {
		fontSize: getRemFromPx(36),
		lineHeight: getRemFromPx(43)
	}
};
DEFAULT_THEME.overrides!.MuiListItemText!.primary = {
	...DEFAULT_THEME.overrides?.MuiListItemText?.primary,
	[DEFAULT_THEME.breakpoints.down('sm')]: {
		fontSize: getRemFromPx(16),
		lineHeight: getRemFromPx(24)
	}
};

// A function that routes the user to the right place
// after login
const onRedirectCallback = (appState: any): void => {
	history.push(appState && appState.targetUrl ? appState.targetUrl : window.location.pathname);
};

ReactDOM.render(
	<StylesProvider generateClassName={generateClassName}>
		<MuiThemeProvider theme={DEFAULT_THEME}>
			<Auth0Provider onRedirectCallback={onRedirectCallback}>
				<Router />
			</Auth0Provider>
		</MuiThemeProvider>
	</StylesProvider>,
	rootEl
);
